import { useCallback, useMemo } from 'react';
import { orderBy } from 'lodash-es';
import { createPrimaryWarningHandler } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks/useResourceWarnings/handlers';
import { ResourceWarning } from '@lp/features/CaseOverview/components/Tabs/AssignedResources/components/AssignResourcesModal/hooks/useResourceWarnings/types';
import { getCoordinatorColumns } from './config';
import { CoordinatorRowData, renderCoordinatorTableCell } from './renderCoordinatorTableCell';
import { useCoordinatorsForAssignment } from '@/queries/resource';
import { Case, CaseUserGroup } from '@/api/apis/case';
import { searchCandidates } from '../ClinicalSpecialistsTable/utils';
import { CandidatesTable } from '../components/CandidatesTable/CandidatesTable';
import { EmployeeRole } from '@/api/apis/resources';

export type ClinicalCoordinatorsTableProps = {
  onAssign: ({ id }: { id: string }) => void;
  toggleWarning: (warning: ResourceWarning) => void;
  idsToAssign?: string[];
  searchTerm: string;
  caseInfo: Case;
};

export const ClinicalCoordinatorsTable = ({
  onAssign,
  idsToAssign = [],
  searchTerm,
  caseInfo,
  toggleWarning,
}: ClinicalCoordinatorsTableProps) => {
  const { assignedEmployees } = caseInfo;
  const coordinators = assignedEmployees[CaseUserGroup.ClinicalCoordinators] || [];
  const assignedEmployeeIds = coordinators.map((e) => e.employeeId).filter(Boolean) as string[];
  const primaryCoordinatorId = coordinators.find((e) => e.isPrimary)?.employeeId;

  const onCCAssign = useCallback(
    ({ employeeId, employeeFullName, isPrimary }: CoordinatorRowData) => {
      if (isPrimary) {
        const updatePrimaryWarning = createPrimaryWarningHandler(toggleWarning);
        updatePrimaryWarning(employeeId, employeeFullName, EmployeeRole.ClinicalCoordinator);
      }
      onAssign({ id: employeeId });
    },
    [onAssign, toggleWarning],
  );

  const { data, isLoading } = useCoordinatorsForAssignment(assignedEmployeeIds);

  const items = useMemo(() => {
    if (!data) return [];
    const mappedCoordinators = data.map((c) => ({
      ...c,
      isLast: idsToAssign.length === 1,
      isPrimary: c.employeeId === primaryCoordinatorId,
      isSelected: idsToAssign.includes(c.employeeId),
    }));
    return orderBy(mappedCoordinators, 'isPrimary', 'desc');
  }, [data, primaryCoordinatorId, idsToAssign]);

  const searchResults = useMemo(() => searchCandidates(searchTerm, items), [searchTerm, items]);

  const renderCell = useMemo(() => renderCoordinatorTableCell(onCCAssign), [onCCAssign]);

  return (
    <CandidatesTable
      label="Clinical coordinators for assignment"
      columns={getCoordinatorColumns()}
      renderCell={renderCell}
      data={searchResults}
      isLoading={isLoading}
      isFetching={isLoading}
    />
  );
};
